import { useQuery } from '@tanstack/react-query'
import { api, isApiError } from '@/api/client'

// Detail codes from app/services/auth/signup_gate.py.
const ACCESS_CODE_REQUIRED = 'signup_access_code_required'
const ACCESS_CODE_INVALID = 'signup_access_code_invalid'

export const ACCESS_CODE_MESSAGE =
  'Maxflow is invite-only right now. Enter your access code to sign up.'
const ACCESS_CODE_INVALID_MESSAGE = "That access code doesn't match. Check it and try again."

/** True when sign-up was refused for a missing or wrong access code. */
export function isAccessCodeError(error: unknown): boolean {
  return (
    isApiError(error) &&
    error.status === 403 &&
    (error.message === ACCESS_CODE_REQUIRED || error.message === ACCESS_CODE_INVALID)
  )
}

/** The message to show for an access-code refusal, or null for any other error. */
export function accessCodeMessage(error: unknown): string | null {
  if (!isAccessCodeError(error)) return null
  return (error as { message: string }).message === ACCESS_CODE_INVALID
    ? ACCESS_CODE_INVALID_MESSAGE
    : ACCESS_CODE_MESSAGE
}

interface SignupConfig {
  signup_requires_access_code: boolean
}

/** Whether a new account needs an access code right now. */
export function useSignupRequiresAccessCode(): boolean {
  const { data } = useQuery({
    queryKey: ['auth', 'signup-config'],
    queryFn: async () => {
      const res = await api.get<SignupConfig>('/auth/signup-config')
      return (res?.data ?? res) as unknown as SignupConfig
    },
    staleTime: 5 * 60 * 1000,
  })
  return data?.signup_requires_access_code ?? false
}
